/**
 * GH#2409 — «Editar propiedad» de la agencia (`/agencia/propiedades/:id/editar`) no guardaba (PATCH 400) y no precargaba
 * pisos, año, condición, sector ni agente.
 *
 * Dos causas, las dos en el front y las dos medidas contra la forma REAL de `GET /properties/:id`:
 *  1. El saga normalizaba la respuesta al modelo viejo y TIRABA lo que el API sí trae plano: `floors`, `yearBuilt`,
 *     `location.neighborhood`, `condition`, `agent.id` (el API no manda `agentId`, manda `agent: { id, fullName }`) y
 *     `location.lat/lng`. Lo que no llegaba al formulario no se podía mostrar.
 *  2. El PATCH mandaba `agentId: ""` (el DTO deja pasar la cadena vacía y la columna es uuid → `InvalidInputSyntax`, 400),
 *     `state` y `zipCode` vacíos (el DTO exige 1–100 y 1–20 caracteres) y `null` en lo que el formulario no cargó.
 *
 * Todo lo de acá es puro, para probarlo sin montar el formulario ni Redux.
 */
import { numero } from '@/features/agente/api/propiedad-del-api';
import { mensajeError } from '@/features/properties-shared/api/crear-propiedad';

export interface PropertyFormValues {
  title: string;
  description: string;
  propertyType: string;
  status: string;
  condition: string;
  images: Array<File | string>;
  price: {
    amount: number | null;
    currency: string;
    period: string;
  };
  area: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parkingSpaces: number | null;
  floors: number | null;
  constructionYear: number | null;
  address: string;
  city: string;
  sector: string;
  coordinates: {
    lat: number | null;
    lng: number | null;
  };
  amenities: string[];
  videos: File[];
  documents: File[];
  agentId: string;
  /** Solo para PINTAR el agente asignado en el desplegable (GH#2449); NO viaja en el PATCH. */
  agentName?: string;
  isFeatured: boolean;
  isUrgent: boolean;
  notes: string;
}

export const VALORES_VACIOS: PropertyFormValues = {
  title: '',
  description: '',
  propertyType: '',
  status: '',
  condition: '',
  images: [],
  price: { amount: null, currency: '', period: 'one_time' },
  area: null,
  bedrooms: null,
  bathrooms: null,
  parkingSpaces: null,
  floors: null,
  constructionYear: null,
  address: '',
  city: '',
  sector: '',
  coordinates: { lat: null, lng: null },
  amenities: [],
  videos: [],
  documents: [],
  agentId: '',
  agentName: '',
  isFeatured: false,
  isUrgent: false,
  notes: '',
};

/**
 * GH#2444 — catálogo ÚNICO de lo que el formulario de edición ofrece en «Tipo» y «Estado». Lo lee el select
 * (`PropertyBasicInfo`) Y los mapas de abajo, así que lo que se ofrece y lo que se traduce no pueden divergir. Antes el
 * select ofrecía 10 de los 18 tipos del API y 5 de los 8 estados: una propiedad de un tipo o estado sin opción abría con
 * el select vacío y había que elegir otro para guardar (el original se perdía).
 * `value` es el del select, `api` el de `PropertyUpdateDTO`/`PropertyStatus`, `clave` la de i18n y `label` el español por defecto.
 */
export interface OpcionCatalogo {
  value: string;
  api: string;
  clave: string;
  label: string;
}

export const TIPOS_EDICION: readonly OpcionCatalogo[] = [
  { value: 'apartamento', api: 'apartment', clave: 'apartamento', label: 'Apartamento' },
  { value: 'penthouse', api: 'penthouse', clave: 'penthouse', label: 'Penthouse' },
  { value: 'estudio', api: 'studio', clave: 'estudio', label: 'Estudio' },
  { value: 'casa', api: 'house', clave: 'casa', label: 'Casa' },
  { value: 'dúplex', api: 'duplex', clave: 'duplex', label: 'Dúplex' },
  { value: 'casa_adosada', api: 'townhouse', clave: 'casa_adosada', label: 'Casa adosada' },
  { value: 'villa', api: 'villa', clave: 'villa', label: 'Villa' },
  { value: 'finca', api: 'finca', clave: 'finca', label: 'Finca' },
  { value: 'solar', api: 'land', clave: 'solar', label: 'Solar' },
  { value: 'desarrollo_terreno', api: 'land_development', clave: 'desarrollo_terreno', label: 'Desarrollo de terrenos' },
  { value: 'local', api: 'commercial', clave: 'local', label: 'Local' },
  { value: 'oficina', api: 'office', clave: 'oficina', label: 'Oficina' },
  { value: 'almacen', api: 'warehouse', clave: 'almacen', label: 'Almacén / Nave' },
  { value: 'industrial', api: 'industrial', clave: 'industrial', label: 'Industrial' },
  { value: 'edificio', api: 'building', clave: 'edificio', label: 'Edificio' },
  { value: 'resort', api: 'resort', clave: 'resort', label: 'Resort' },
  { value: 'hotel', api: 'hotel', clave: 'hotel', label: 'Hotel' },
  { value: 'marina', api: 'marina', clave: 'marina', label: 'Marina' },
];

export const ESTADOS_EDICION: readonly OpcionCatalogo[] = [
  { value: 'disponible', api: 'available', clave: 'disponible', label: 'Disponible' },
  { value: 'reservado', api: 'reserved', clave: 'reservado', label: 'Reservado' },
  { value: 'vendido', api: 'sold', clave: 'vendido', label: 'Vendido' },
  { value: 'alquilado', api: 'rented', clave: 'alquilado', label: 'Alquilado' },
  { value: 'negociacion', api: 'negotiation', clave: 'negociacion', label: 'En negociación' },
  { value: 'alquiler_temporal', api: 'temp_rental', clave: 'alquiler_temporal', label: 'Alquiler temporal' },
  { value: 'mantenimiento', api: 'off_market', clave: 'mantenimiento', label: 'En Mantenimiento' },
  { value: 'borrador', api: 'draft', clave: 'borrador', label: 'Borrador' },
];

const ESTADO_FORM_A_API: Record<string, string> = Object.fromEntries(ESTADOS_EDICION.map((o) => [o.value, o.api]));
/** `inactive` es el alias viejo de «mantenimiento» (el API lo normaliza a `off_market`). */
const ESTADO_API_A_FORM: Record<string, string> = {
  ...Object.fromEntries(ESTADOS_EDICION.map((o) => [o.api, o.value])),
  inactive: 'mantenimiento',
};
const TIPO_FORM_A_API: Record<string, string> = Object.fromEntries(TIPOS_EDICION.map((o) => [o.value, o.api]));
const TIPO_API_A_FORM: Record<string, string> = Object.fromEntries(TIPOS_EDICION.map((o) => [o.api, o.value]));

/**
 * El select de condición describe el ESTADO FÍSICO (excelente, buena…) y el API la ETAPA (nueva, usada…): solo «nueva»
 * tiene equivalente exacto. «Usada» NO se traduce a «buena»: sería inventar un dato.
 */
const condicionDeApi = (v: unknown): string => (v === 'new' ? 'nueva_construccion' : '');

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Propiedad normalizada por el saga (o, si algún día llega cruda, la respuesta del API) → valores del formulario. */
export function formularioDesdePropiedad(p: any): PropertyFormValues {
  const loc = p?.location ?? {};
  const coords = loc.coordinates ?? {};
  return {
    title: texto(p?.title),
    description: texto(p?.description),
    propertyType: TIPO_API_A_FORM[p?.propertyType ?? p?.type ?? ''] ?? '',
    status: ESTADO_API_A_FORM[p?.status ?? ''] ?? '',
    condition: condicionDeApi(p?.condition),
    images: Array.isArray(p?.images)
      ? p.images.map((i: any) => (typeof i === 'string' ? i : i?.url)).filter(Boolean)
      : [],
    price: {
      // TypeORM serializa los decimal como texto ("175981.24"): sin Number() el PATCH mandaba price como texto.
      amount: numero(p?.price),
      currency: texto(p?.currency),
      period: 'one_time',
    },
    area: numero(p?.size) ?? numero(p?.coveredAreaM2) ?? numero(p?.features?.area),
    bedrooms: numero(p?.bedrooms),
    bathrooms: numero(p?.bathrooms),
    parkingSpaces: numero(p?.parkingSpaces) ?? numero(p?.features?.parking),
    floors: numero(p?.floors) ?? numero(p?.features?.totalFloors),
    constructionYear: numero(p?.yearBuilt) ?? numero(p?.features?.yearBuilt),
    address: texto(loc.address),
    city: texto(loc.city),
    sector: texto(loc.neighborhood),
    coordinates: {
      lat: numero(coords.lat) ?? numero(loc.lat),
      lng: numero(coords.lng) ?? numero(loc.lng),
    },
    amenities: Array.isArray(p?.amenities) ? p.amenities : [],
    videos: [],
    documents: [],
    agentId: texto(p?.agentId) || texto(p?.agent?.id),
    agentName: texto(p?.agentName) || texto(p?.agent?.fullName),
    isFeatured: p?.isFeatured === true,
    isUrgent: p?.isUrgent === true,
    // Antes caía a la descripción: al guardar se copiaba la descripción a las notas sin que nadie la escribiera.
    notes: texto(p?.metadata?.notes),
  };
}

/**
 * GH#2449 — el desplegable de agente sale en «Seleccionar…» aunque el API trae el agente de la propiedad. Sus opciones
 * vienen de `GET /agency/agents/dropdown` (tabla de agentes, límite 50) y el `agent.id` de la propiedad puede no estar ahí
 * (otro id, o más de 50 agentes). Si el agente actual no está en la lista, se agrega como primera opción con el nombre que
 * ya trae la propiedad: el select lo muestra y el valor que se manda es el mismo que ya viajaba. Sin agente, no cambia nada.
 */
export function opcionesDeAgente<T extends { value: string; label: string }>(
  opciones: T[],
  agenteActualId?: string,
  agenteActualNombre?: string,
): Array<T | { value: string; label: string }> {
  const id = (agenteActualId ?? '').trim();
  if (!id || opciones.some((o) => o.value === id)) return opciones;
  const nombre = (agenteActualNombre ?? '').trim();
  return [{ value: id, label: nombre || 'Agente asignado' }, ...opciones];
}

const vacio = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (typeof v === 'number' && !Number.isFinite(v));

/**
 * Cuerpo del `PATCH /properties/:id`: campos PLANOS de `PropertyUpdateDTO`, sin `features` (en el API es una lista de
 * textos, no el objeto del formulario) y SIN lo vacío: `agentId: ""` daba 400 y un `null` en pisos o año es «no lo cargué»,
 * no «bórralo». Quien deja un número en blanco no lo borra; simplemente no se manda.
 *
 * `condition` e `isUrgent` NO viajan: `PropertyUpdateDTO` no los declara y el whitelist los descarta en silencio.
 */
export function cuerpoActualizarPropiedad(v: PropertyFormValues, original?: any): Record<string, unknown> {
  const cuerpo: Record<string, unknown> = {
    title: v.title?.trim(),
    description: v.description,
    propertyType: TIPO_FORM_A_API[v.propertyType] ?? v.propertyType,
    status: ESTADO_FORM_A_API[v.status] ?? v.status,
    price: v.price?.amount,
    currency: v.price?.currency,
    size: v.area,
    bedrooms: v.bedrooms,
    bathrooms: v.bathrooms,
    parkingSpaces: v.parkingSpaces,
    floors: v.floors,
    yearBuilt: v.constructionYear,
    address: v.address?.trim(),
    city: v.city,
    // El formulario no pide provincia ni código postal: se conservan los de la propiedad, y si no hay, no se mandan.
    state: original?.location?.state,
    zipCode: original?.location?.zipCode,
    neighborhood: v.sector,
    latitude: v.coordinates?.lat,
    longitude: v.coordinates?.lng,
    amenities: v.amenities,
    isFeatured: v.isFeatured,
    agentId: v.agentId?.trim(),
  };
  for (const k of Object.keys(cuerpo)) if (vacio(cuerpo[k])) delete cuerpo[k];
  // `metadata` es una columna JSON que el PATCH REEMPLAZA entera (Object.assign en PropertyService.update), y el alta guarda
  // ahí `commercialConditions`, `virtualTourUrl`, `videoUrl` y `attachments`. Mandar `{ notes }` solo los borraba: viaja
  // MEZCLADO con lo original, y solo si las notas cambiaron (si no hay nada que cambiar, no viaja).
  const notasOriginales = typeof original?.metadata?.notes === 'string' ? original.metadata.notes : '';
  const notasNuevas = typeof v.notes === 'string' ? v.notes : '';
  if (notasNuevas !== notasOriginales) cuerpo.metadata = { ...(original?.metadata ?? {}), notes: notasNuevas };
  // La descripción SÍ puede dejarse en blanco a propósito: si la propiedad tenía una y ahora no, se manda vacía para borrarla.
  if (!vacio(v.description) || !vacio(original?.description)) cuerpo.description = v.description ?? '';
  return cuerpo;
}

/**
 * Motivo de una falla para quien la lee. El saga mostraba `error.message`, que en axios es «Request failed with status
 * code 400» (inglés, sin motivo) y tapaba el `message` que sí manda el API. Primero lo del servidor; si hubo respuesta
 * pero sin mensaje, el genérico en español; nunca el texto de axios.
 */
export function mensajeDeFalla(error: unknown, fallback: string): string {
  const delServidor = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return mensajeError(delServidor, fallback);
}
